import { FontFamilyProps } from '../const/font';

export enum questionSpecialIdentifiers {
  PERSON_TYPE = 'person_type',
  FLOW = 'flow',
}

export enum questionFieldType {
  LABEL = 'label',
  TEXT = 'text',
  RADIO = 'radio',
  TEXT_AUTOCOMPLETE = 'text_autocomplete',
  DESCRIPTION_TEXT = 'description_text',
  DATE_COMPLETE = 'date_complete',
  DROPDOWN = 'dropdown',
  TEXTAREA = 'textarea',
  RADIO_VERTICAL = 'radio_vertical',
  CHECKBOX = 'checkbox',
  BUTTON = 'button',
  DATE_PARTIAL = 'date_partial',
  INPUT_DROPDOWN = 'input_dropdown',
  DIALOG_BOX = 'dailog_box',
  MANUAL_DIALOG_BOX = 'manual_dialog_box',
  // custom field type for person type
  PERSON_TYPE = 'person_type',
  // custom field type for form flow select
  FLOW_TYPE = 'flow_type',
  // custom field type for notification text
  NOTIFICATION = 'notification',
  // custom field type for vaccination info array
  COMPLEX_FIELD = 'complex_field',
  //duplicator
  DUPLICATOR = 'duplicator',
  // instead of hardcoded CustomFlowID::doc_info
  DOC_UPLOAD = 'doc_upload',
  GOOGLE_AUTOCOMPLETE = 'google_autocomplete',
}

export enum NotificationType {
  SUCCESS = 'success',
  ERROR = 'error',
}

export const OptionSortingField = 'orderBy';
export type Option = {
  identifier?: string;
  option?: string;
  tooltip?: string;
  information?: string;
  image?: string;
  altText?: string;
  // dropdown props
  value?: string;
  e2b_code?: string;
  iso_code?: string;
  study_no?: string;
  ongoing?: boolean | null;
  unknown?: string | null;
  [OptionSortingField]?: string | number | null;
};

export enum QuestionRequired {
  YES = 'yes',
  NO = 'no',
}

export type DateType = {
  formatMessage?: string;
  value?: string;
  format?: string;
  futureDateMessage?: string;
  startDateMessage?: string;
  endDateMessage?: string;
  eventDobMessage?: string;
  eventDeathMessage?: string;
};

export enum ConditionAttributes {
  QUESTION = 'question',
  DEFAULT = 'value',
  VISIBLE = 'hidden',
  FILE = 'file',
  REQUIRED = 'required',
  HINTS = 'field_hints',
  DISABLED = 'disabled',
}

export const exportedConditionAttributes = [ConditionAttributes.HINTS, ConditionAttributes.QUESTION];

export enum ValidationTypes {
  EMAIL = 'email',
  FLOAT = 'float',
  INTEGER = 'integer',
  MAXLENGTH = 'max_length',
  MINLENGTH = 'min_length',
  MINNUMBER = 'min_number',
  MAXNUMBER = 'max_number',
  NUMERIC = 'numeric',
  PREG = 'preg',
  REQUIRED = 'required',
  MAXDATE = 'max_date',
  MINDATE = 'min_date',
  AGE = 'age',
}

export interface ConditionParent {
  identifier: string;
  value: string;
  isEmpty?: boolean;
}

export interface Condition {
  persona?: string;
  event?: string;
  parent: ConditionParent[];
  attribute: ConditionAttributes;
  value: string | boolean | string[];
}

export interface Validation {
  type?: ValidationTypes;
  message?: string;
  min?: number;
  max?: number;
  value?: number | RegExp | string;
}

type CustomAttribute = {
  label: string;
  value: string | null;
};

export enum SortOptions {
  NONE = 'none',
  ALPHABETICAL = 'alphabetical',
  PARAMETERIZED = 'parameterized',
}

export type DocumentsMeta = {
  classification: {
    personas: string[];
    events: string[];
  };
  description: {
    personas: string[];
    events: string[];
  };
};

export type FileReports = {
  value: string;
  description?: string;
};

export type Question = {
  id: string;
  field_type: questionFieldType;
  conditions: Condition[];
  question: string;
  identifier: string;
  sub_question: string;
  personas: string[];
  events: string[];
  field_hints?: string[];
  disabled?: boolean;
  hidden?: boolean;
  value?: string | string[];
  placeholder?: string;
  tooltip?: string;
  required: QuestionRequired;
  chunkedFile?: boolean;
  file?: string;
  options?: Option[];
  depends_on?: string;
  additional_value?: {
    value?: string;
    withdraw_date?: string;
    display?: number;
  };
  children?: Question[];
  src?: string;
  validations?: Validation[];
  duplicatorGroup?: string;
  customAttributes?: CustomAttribute[];
  sortStrategy?: SortOptions;
  maxSections?: number;
  documentsMeta?: DocumentsMeta;
  isPrivacy?: boolean;
};

export type FooterMenuLink = {
  link: string;
  label: string;
  newTab: boolean;
};
export type ConfigFile = {
  buildVersion: string;
  version: number;
  portal_type: string;
  title: string;
  favicon: string;
  description: string;
  showTranslation: boolean;
  preventReload: boolean;
  personaQuestion: Question;
  eventQuestion: Question;
  header: {
    logo: string;
    showCompanyName: boolean;
    companyName: string;
    appName: string;
    languages: {
      image: string;
      dropdown: {
        name: string;
        short_name: string;
      }[];
    };
  };
  theme: {
    headerColor: string;
    footerColor: string;
    btnLight: {
      bg: string;
      bgHover: string;
      color: string;
      colorHover: string;
    };
    btnDark: {
      bg: string;
      bgHover: string;
      color: string;
      colorHover: string;
    };
    font: {
      family: FontFamilyProps;
    };
  };
  Ascii_replace: {
    [key: string]: string;
  };
  footer: {
    copyright_text: string;
    rights_reserved: string;
    legal_notice: string;
    logo: string;
    menu: FooterMenuLink[];
    footerText?: string;
  };
  home: {
    reportInfo: string;
    contactInfo: string;
    images: {
      home?: {
        url?: string;
        altText?: string;
      };
      side?: {
        url?: string;
        altText?: string;
      };
    };
  };
  pages: {
    legalNotice: string;
    japanLegalNotice: string;
    koreanLegalNotice: string;
  };
  mandatory: {
    mandatoryAll: string;
    mandatory_message: string;
  };
  errors: {
    errorKey: string;
    uploadSizeLimit: string;
    uploadSupportedFormats: string;
    uploadPregnancySupportedFormat: string;
    uploadCountLimit: string;
  };
  success: {
    successKey: string;
  };
  monthNames: {
    Jan: string;
    Feb: string;
    Mar: string;
    Apr: string;
    Jun: string;
    Jul: string;
    Aug: string;
    Sep: string;
    Oct: string;
    Nov: string;
    Dec: string;
  };
  confirmation: {
    confirmed: string;
    thankyou: string;
    identificationNo: string;
    timestamp: string;
    print: string;
    reportAdverse: string;
  };
  keywords: {
    description: string;
    studyName: string;
    docClassification: string;
    captchaChar: string;
    captchaFail: string;
    typeStepLabel: string;
    confirmStepLabel: string;
    fileChoose: string;
    fileRemove: string;
    docDescription: string;
    uploaded: string;
    uploadMaxSize: number;
    uploadMaxCount: number;
    pregnancyComplete: string;
    sessionInactivity: string;
    okayButton: string;
    idelTime: string;
    continueButton: string;
    pregnancyFail: string;
    timeout: string;
    reloadButtonError: string;
    reloadButton: string;
    browserClose: string;
    idelTimoutsec: number;
    timeoutSec: number;
    backToPortal: string;
    previous: string;
    next: string;
    submit: string;
    uploadKey: string;
    fileReports: FileReports[];
    duplicatedTabTitle: string;
    duplicatedTabText: string;
    captchaInfo: string;
    captchaNote: string;
    choosePersona: string;
    nofileChosenText: string;
  };
  questions: Question[];
  drugExposure: string;
};

export const QuestionsCustomAttribute = [
  questionFieldType.DATE_COMPLETE,
  questionFieldType.DATE_PARTIAL,
  questionFieldType.TEXT,
];

export const QuestionsWithoutCustomInput = [
  questionFieldType.CHECKBOX,
  questionFieldType.RADIO,
  questionFieldType.RADIO_VERTICAL,
  questionFieldType.TEXT_AUTOCOMPLETE,
  questionFieldType.PERSON_TYPE,
  questionFieldType.FLOW_TYPE,
  questionFieldType.DROPDOWN,
];

// TODO find better solution on backend
export enum CustomFlowId {
  PERSON_TYPE = 'person_type',
  PRODUCT_REPORTING = 'product_reporting',
  REPORT_SELECT = 'report_select',
  DOC_INFO = 'doc_info',
  SUBMIT = 'summ_submit',
  STUDY_PRODUCT = 'study_product',

  PATIENT_AGE = 'patient_age',
  AGE_UNIT = 'age_unit',
  AGE_GROUP = 'age_group',

  ROUTE_ADMIN = 'route_admin',

  PREGNANCY_FORM = 'pregnancy_form',

  //COVID flow hack
  ADDITIONAL_EVENTS = 'additional_events',
}

export interface GridBreakpointsScale {
  xs?: number | string;
  sm: number | string;
  md: number | string;
  lg: number | string;
  xl: number | string;
}
export const MAGIC_STUDY_TYPE = 3;
export const MAGIC_STUDY_PRODUCT_FILE = 'assets/dropdown_files/study_product.json';
